import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { Flex } from 'styling/GlobalStyles'
import '../HonestSelect/HonestSelect.css'
import { fetchAuthSession } from '@aws-amplify/auth';
import axios from 'axios'
import Chevron from 'assets/icons/Chevron'
import { handleFocusNext, removeDuplicates } from 'utils/helpers'
import { ISelectValue } from 'constants/globalTypes'
import { IHonestAsyncSelect } from 'types/select.interface'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { BodyText } from 'styling/Typography';
import { MultiSingle, OptionsContainer, Select, SelectInput, SelectOption, Placeholder } from 'components/HonestSelect/HonestSelect.styled';

export const HonestAsyncSelect = ({
  onChange,
  width = 'auto',
  defaultValue,
  link,
  name,
  placeholder,
  invalid,
  parserFunction,
  fixedList,
  multi,
  handleRemove,
  noPagination,
  additionalParams,
  onBlur,
  query,
  setQuery,
  blured,
  setOpenOptions,
  openOptions,
}: IHonestAsyncSelect) => {

  const [page, setPage] = useState(1)
  const [selectedOption, setSelectedOption] = useState(
    multi && defaultValue?.length > 0 ? defaultValue : defaultValue && !multi ? defaultValue : '',
  )
  const [options, setOptions] = useState<{ label: string, value: number, disabled?: boolean, suboptions?: { value: number, label: string }[] }[]>([])
  const ref = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  })
  const [selectedMulti, setSelectedMulti] = useState(
    multi && defaultValue
      ? defaultValue.map((item: ISelectValue) => ({ label: item.label, value: item.value }))
      : [],
  )
  const [selected, setSelected] = useState(defaultValue ? true : false)

  useEffect(() => {
    const updatePosition = () => {
      if (!ref.current || !openOptions) return

      const rect = ref.current.getBoundingClientRect()
      const menuHeight = Math.min(options.length * 40, 250)
      const shouldShowAbove = rect.bottom + menuHeight > window.innerHeight && rect.top > menuHeight

      setPosition({
        top: shouldShowAbove ? rect.top - menuHeight : rect.bottom,
        left: rect.left,
        width: rect.width
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [openOptions, options.length])

  useEffect(() => {
    if (parserFunction) {

      const params = new URLSearchParams();

      if (!noPagination) {
        params.append('page', page.toString());
        params.append('limit', '10');

        if (query) {
          params.append('q', query);
        }
      }

      if (additionalParams) {
        Object.entries(additionalParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(
              key,
              value instanceof Date ? value.toISOString() : String(value)
            );
          }
        });
      }


      const fetchData = async () => {
        const session = await fetchAuthSession()
        link &&
          axios.get(
            `${process.env.REACT_APP_API}/${link}${params.toString() ? '?' + params.toString() : ''}`,
            { headers: { Authorization: `Bearer ${session.tokens.accessToken}` } }
          )
            .then((res) => {
              setOptions((prev) => {
                let copy = [...prev]
                copy = [...copy, ...parserFunction(res.data)].filter(({ label }) => label.toLowerCase().indexOf(query.toLowerCase()) > -1)
                if (noPagination) {
                  copy = copy.filter(({ label }) => label.toLowerCase().indexOf(query.toLowerCase()) > -1)
                }
                return removeDuplicates(copy)
              })
            })
      }
      fetchData()
    } else {
      searchFixedList()
    }
  }, [page, query, additionalParams, link, parserFunction, noPagination])

  useEffect(() => {
    if (!fixedList) {
      setOptions([])
    }
  }, [additionalParams, fixedList])

  useEffect(() => {
    if (defaultValue) {
      setSelectedOption(defaultValue)
      setSelected(true)
      setQuery('')
    } else {
      setSelected(false)
      setSelectedOption(multi ? [] : '')
      setQuery('')
    }
  }, [defaultValue])

  const searchFixedList = () => {
    const list =
      fixedList &&
      fixedList.filter(({ label }) => label.toLowerCase().indexOf(query.toLowerCase()) > -1)
    setOptions(list)
  }

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollHeight, scrollTop, offsetHeight } = e.currentTarget
    const bottom = scrollTop + offsetHeight >= scrollHeight
    if (bottom && !noPagination) {
      setPage((prev) => prev + 1)
    }
  }

  const handleQuery = (e: React.FormEvent<HTMLInputElement>) => {
    setOptions([])
    const { value } = e.target as HTMLInputElement
    setSelectedOption(value)
    setQuery((prev: string) => {
      if (prev.length > value.length) {
        setSelected(false)
      }
      return value
    })
    if (query.length > value.length) {
      onChange({ label: '', value: null }, name)
    }
    setPage(1)
  }

  const handleChange = (value: ISelectValue) => {
    if (value.value) {
      if (multi) {
        setSelectedMulti((prev: ISelectValue[]) => {
          const copy = [...prev]
          copy.push(value)
          return copy
        })
      } else {
        // setQuery(value.label) TODO ispitati sto je ovo bilo ubaceno uopste
        setSelectedOption(value.label)
      }
      onChange(value, name)
      setOpenOptions(false)
    }
  }

  const handleClear = () => {
    if (multi) {
      setSelectedMulti([])
      onChange({ label: 'CLEAR', value: null }, name)
    } else {
      onChange({ label: '', value: null }, name)
      setPage(1)
      setQuery('')
      setSelectedOption('')
      setSelected(false)
    }
  }

  const handleMultiRemove = (item: ISelectValue) => {
    setSelectedMulti((prev: ISelectValue[]) => {
      let copy = [...prev]
      copy = copy.filter((i) => i.value !== item.value)
      return copy
    })
    handleRemove(item)
  }

  let cName = 'honest-select-container'
  if ((!multi && selectedOption) || (multi && selectedOption.length > 0)) {
    cName = cName + ' active-select'
  }

  const renderDropdown = () => {
    if (!openOptions) return null

    const dropdownStyle: React.CSSProperties = {
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      width: `${position.width}px`,
      maxHeight: '250px',
      overflowY: 'auto',
      zIndex: 1000,
    }

    return ReactDOM.createPortal(

      <OptionsContainer
        style={dropdownStyle}
        ref={scrollContainerRef}
        onClick={e => e.stopPropagation()}
        onScroll={handleScroll}
      >

        {options?.length ?
          options
            .filter((item) => !selectedMulti.some((i: ISelectValue) => i.value == item.value))
            .map((item, key) => (
              <Flex key={key} column>
                <SelectOption disabled={!!item.suboptions?.length || item.disabled} onPointerDown={(e) => {
                  e.preventDefault()
                  handleChange(item)
                  setSelected(true)
                }}
                  key={key} >
                  <BodyText>{item.label}</BodyText>
                </SelectOption>
                {!!item.suboptions?.length && <Flex column  >
                  {item.suboptions.map(i => (
                    <SelectOption

                      onPointerDown={(e) => {
                        e.preventDefault()
                        handleChange(i)
                        setSelected(true)
                      }}
                      key={i.value} >
                      <BodyText style={{ marginLeft: 16 }} >{i.label}</BodyText>
                    </SelectOption>
                  ))}
                </Flex>}
              </Flex>

            )) : <Flex width='100%' center justcenter padding='5px'> <BodyText>No Options</BodyText></Flex>}
      </OptionsContainer>,
      document.body
    )
  }

  return (
    <Select
      tabIndex={1}
      onBlur={onBlur}
      onClick={() => {
        if (!openOptions) {
          setOpenOptions(true)
        }
      }}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
      }}

      className={cName}
      ref={ref}
      onKeyDown={handleFocusNext}
      value={selected}
      invalid={invalid}
      active={openOptions}
      blured={blured}
    >
      <Flex>  {/* Icons container */}

      </Flex>
      <Flex style={{ flex: 1 }} >

        {multi ? (
          <>
            {selectedMulti.length < 1 && <Placeholder>Select</Placeholder>}
            <div className='multi-container'>
              {selectedMulti.map((item: ISelectValue, key: number) => (
                <MultiSingle key={key} className='multi-single'>
                  <Placeholder color='#fff' >{item.label}</Placeholder>
                  {selectedMulti.length > 0 && (
                    <div onMouseDown={() => handleMultiRemove(item)} className='close-container'>
                      <CloseIcon />
                    </div>
                  )}
                </MultiSingle>
              ))}
            </div>
          </>
        ) : (
          <SelectInput
            type='text'
            className='select-search'
            value={selectedOption}
            placeholder={placeholder ? placeholder : 'Select...'}
            onChange={handleQuery}
          />
        )
        }
      </Flex>


      <Flex  >
        <Chevron />
        {selected ? (
          <CloseIcon onClick={handleClear} />
        ) : null}
      </Flex>
      {renderDropdown()}
    </Select >
  )
}
